/** The bucket-backed project or workspace that a Documents browser is rooted in.
 *  The scope travels through the hooks and uploader as a single string key, so a project id is still just the id and a
 *  workspace is `workspace:<id>`; this adapter turns the key into the right routes and the right feature set. */
import { SUPABASE_URL } from '../../utils/supabase/url';
import type { RoleAccess } from './documentPermissionUtils';

export type DocumentScope =
  | { kind: 'project'; projectId: string }
  | { kind: 'workspace'; workspaceId: string };

export const WORKSPACE_SCOPE_PREFIX = 'workspace:';

export function scopeKey(scope: DocumentScope): string {
  return scope.kind === 'project' ? scope.projectId : `${WORKSPACE_SCOPE_PREFIX}${scope.workspaceId}`;
}

export function scopeFromKey(key: string): DocumentScope {
  return key.startsWith(WORKSPACE_SCOPE_PREFIX)
    ? { kind: 'workspace', workspaceId: key.slice(WORKSPACE_SCOPE_PREFIX.length) }
    : { kind: 'project', projectId: key };
}

export interface DocumentScopeFeatures {
  /** Document number, revision, status and discipline, and the revision register built on them. */
  documentControl: boolean;
  stars: boolean;
  externalShare: boolean;
  folderPermissions: boolean;
  tenderLetter: boolean;
  /** Header "new folder" action. */
  createFolder: boolean;
  /** The list-files-batch route exists; without it folders are listed one request each. */
  batchListing: boolean;
}

export interface DocumentScopeAdapter {
  scope: DocumentScope;
  key: string;
  isWorkspace: boolean;
  features: DocumentScopeFeatures;
  /** Name of the root crumb. */
  rootLabel: string;
  /** Full URL of a folder route, written with its project name (`list-files`, `download/<id>`, ...). */
  url: (path: string) => string;
  /** The identifying query or form field the project routes expect; workspace routes carry it in the path. */
  fields: () => Record<string, string>;
  /** Sets the scope's identifying fields on a query. */
  applyTo: (params: URLSearchParams) => URLSearchParams;
  /** The route that returns the root folder (and, for a workspace, whether the caller can manage it). */
  rootUrl: () => string;
}

const API = () => `${SUPABASE_URL}/functions/v1/make-server-a476273b`;

const PROJECT_FEATURES: DocumentScopeFeatures = {
  documentControl: true, stars: true, externalShare: true, folderPermissions: true, tenderLetter: true, createFolder: false, batchListing: true,
};
const WORKSPACE_FEATURES: DocumentScopeFeatures = {
  documentControl: false, stars: true, externalShare: false, folderPermissions: false, tenderLetter: false, createFolder: true, batchListing: false,
};

const cache = new Map<string, DocumentScopeAdapter>();

function workspaceAdapter(scope: Extract<DocumentScope, { kind: 'workspace' }>, key: string): DocumentScopeAdapter {
  return {
    scope, key, isWorkspace: true, features: WORKSPACE_FEATURES, rootLabel: 'Workspace Files',
    url: (path) => `${API()}/workspace-folders/${encodeURIComponent(scope.workspaceId)}/${path}`,
    fields: () => ({ workspaceId: scope.workspaceId }),
    applyTo: (params) => { params.set('workspaceId', scope.workspaceId); return params; },
    rootUrl: () => `${API()}/workspace-folders/${encodeURIComponent(scope.workspaceId)}`,
  };
}

function projectAdapter(scope: Extract<DocumentScope, { kind: 'project' }>, key: string): DocumentScopeAdapter {
  return {
    scope, key, isWorkspace: false, features: PROJECT_FEATURES, rootLabel: 'Project Files',
    url: (path) => `${API()}/project-folders/${path}`,
    fields: (): Record<string, string> => (scope.projectId ? { projectId: scope.projectId } : {}),
    applyTo: (params) => { if (scope.projectId) params.set('projectId', scope.projectId); return params; },
    rootUrl: () => `${API()}/project-folders/${encodeURIComponent(scope.projectId)}`,
  };
}

export function documentScopeAdapter(input: DocumentScope | string): DocumentScopeAdapter {
  const key = typeof input === 'string' ? input : scopeKey(input);
  const hit = cache.get(key);
  if (hit) return hit;
  const scope = typeof input === 'string' ? scopeFromKey(input) : input;
  const adapter = scope.kind === 'workspace' ? workspaceAdapter(scope, key) : projectAdapter(scope, key);
  cache.set(key, adapter);
  return adapter;
}

/** What the server says the caller may do in a workspace tree. */
export interface WorkspaceDocumentCapabilities {
  canView: boolean;
  canUpload: boolean;
  canManage: boolean;
}

/** Workspace access is the server's answer alone: everyone who can open the area sees every folder, and write
 *  rights come from the capability flags (a former client's staff get neither). */
export function workspaceDocumentAccess<T extends {
  roleAccess: RoleAccess; hasFullAccess: boolean; canUpload: boolean; canEdit: boolean; canDelete: boolean;
}>(role: T, caps: Pick<WorkspaceDocumentCapabilities, 'canUpload' | 'canManage'>): T {
  const canUpload = caps.canUpload || caps.canManage;
  return {
    ...role,
    hasFullAccess: true,
    canUpload,
    canEdit: caps.canManage,
    canDelete: caps.canManage,
    roleAccess: { folders: ['*'], permission: canUpload ? 'writer' : 'reader' },
  };
}
